import client from './client';

const PATH = '/api/parivars/parivars/';

/** True when a payload carries a file, i.e. it must go out as multipart/form-data. */
const hasFile = (data) => data && Object.values(data).some((value) => value instanceof Blob);

/** Convert a plain object into FormData, skipping empty/undefined values. */
const toFormData = (data) => {
  const fd = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (value instanceof Blob) fd.append(key, value, value.name || key);
    else fd.append(key, value);
  });
  return fd;
};

const send = (method, url, data) => {
  if (data && hasFile(data)) {
    return client[method](url, toFormData(data)).then((r) => r.data);
  }
  return client[method](url, data).then((r) => r.data);
};

/**
 * Active Parivars for the public selector. The backend list endpoint already
 * filters out inactive records for anonymous visitors, and up to 100 rows is
 * plenty for the foreseeable Parivar count.
 */
const list = (params = {}) =>
  client.get(PATH, { params: { page_size: 100, ...params } }).then((r) => r.data);

/** One active Parivar by slug (same visibility rules as the list). */
const bySlug = (slug) => client.get(`${PATH}slug/${slug}/`).then((r) => r.data);

/** Full Parivar details for /parivar/:slug — core fields + trustees. */
const details = (slug) => client.get(`${PATH}slug/${slug}/details/`).then((r) => r.data);

/** One Parivar by id (super admins see inactive parivars too). */
const get = (id) => client.get(`${PATH}${id}/`).then((r) => r.data);

/** Create a Parivar (Super Admin only) — multipart when an image is included. */
const create = (data) => send('post', PATH, data);

/**
 * Super-admin management list: EVERY Parivar (active + inactive) with the
 * admin-only metadata (is_active, is_default, assigned_admins). Super Admins
 * only; Parivar Admins get a 403. The backend action returns a plain array.
 */
const manageList = () => client.get(`${PATH}manage/`).then((r) => r.data);

/** Update a Parivar's details — multipart when an image file is included. */
const update = (id, data) => send('patch', `${PATH}${id}/`, data);

/** Activate a Parivar (Super Admin only). */
const activate = (id) => client.post(`${PATH}${id}/activate/`).then((r) => r.data);

/** Deactivate a Parivar (Super Admin only; the default Parivar is protected). */
const deactivate = (id) => client.post(`${PATH}${id}/deactivate/`).then((r) => r.data);

/** Trustee list for one Parivar (Super or that Parivar's admin). */
const trustees = (id) => client.get(`${PATH}${id}/trustees/`).then((r) => r.data);

/** Add a trustee to one Parivar (multipart when a photo is included). */
const createTrustee = (id, data) => send('post', `${PATH}${id}/trustees/`, data);

/** Update a trustee of one Parivar (multipart when a photo is included). */
const updateTrustee = (id, trusteeId, data) =>
  send('patch', `${PATH}${id}/trustees/${trusteeId}/`, data);

/** Delete a trustee of one Parivar. */
const deleteTrustee = (id, trusteeId) =>
  client.delete(`${PATH}${id}/trustees/${trusteeId}/`).then((r) => r.data);

/** Reorder one trustee up/down within its Parivar. */
const moveTrustee = (id, trusteeId, direction) =>
  client.post(`${PATH}${id}/trustees/${trusteeId}/move/`, { direction }).then((r) => r.data);

export const parivarApi = {
  list,
  bySlug,
  details,
  get,
  create,
  manageList,
  update,
  activate,
  deactivate,
  trustees,
  createTrustee,
  updateTrustee,
  deleteTrustee,
  moveTrustee,
};
